/**
 * Yielding for long loops on the main thread (OWA-1378).
 *
 * `await new Promise(r => setTimeout(r, 0))` waits at least 4 ms once timers nest, so a loop
 * that yields every 100 rows spent 2.4 to 3.8 s idle per 48,000 rows. A posted message or
 * scheduler.yield() lets input and rendering run without that floor.
 */

type Scheduler = {yield?: () => Promise<void>};

const channel = typeof MessageChannel === 'undefined' ? null : new MessageChannel();
const waiting: (() => void)[] = [];
if (channel) channel.port1.onmessage = () => waiting.shift()?.();

/** Gives the browser a turn (input, rendering, other tasks), then resumes */
export function yieldToMain(): Promise<void> {
    const scheduler = (globalThis as {scheduler?: Scheduler}).scheduler;
    if (typeof scheduler?.yield === 'function') return scheduler.yield();
    if (!channel) return new Promise(resolve => setTimeout(resolve, 0));
    return new Promise(resolve => {
        waiting.push(resolve);
        channel.port2.postMessage(null);
    });
}

/** Default work per slice: short enough that input still feels immediate */
export const SLICE_MS = 10;

/**
 * scheduler.yield() resumes ahead of timers, and in Firefox posted messages also run before
 * a waiting timer. So at least this often a yield goes through setTimeout, which lets the
 * app's own timers run; it costs about 4 ms per TIMER_TURN_MS of work.
 */
export const TIMER_TURN_MS = 100;

/**
 * Returns a function to call once per loop iteration: when the current slice has run for
 * `sliceMs` it returns a promise that yields, and otherwise it returns undefined (no await needed).
 */
export function createYielder(sliceMs = SLICE_MS): () => Promise<void> | undefined {
    let sliceStart = performance.now();
    let lastTimerTurn = sliceStart;
    return () => {
        const now = performance.now();
        if (now - sliceStart < sliceMs) return undefined;
        const timerTurn = now - lastTimerTurn >= TIMER_TURN_MS;
        const turn = timerTurn ? new Promise<void>(resolve => setTimeout(resolve, 0)) : yieldToMain();
        return turn.then(() => {
            sliceStart = performance.now();
            if (timerTurn) lastTimerTurn = sliceStart;
        });
    };
}
